import { useCallback, useEffect, useState } from 'react';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import {
  type InventorizationBlockId,
  updateInventorizationBlock,
} from '@/features/inventorization/domain/inventorization';
import type { InventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import { updateInventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import { dropIssuesResolvedByExtraction } from '@/features/inventorization/domain/inventorization-effective-validation';
import type { InventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import { updateInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import type { InventorizationPosition } from '@/features/inventorization/domain/inventorization-position';
import { updateInventorizationPosition } from '@/features/inventorization/domain/inventorization-position';
import type {
  InventorizationMainReading,
  InventorizationRegisterReading,
} from '@/features/inventorization/domain/inventorization-reading';
import {
  removeInventorizationRegisterReading,
  updateInventorizationMainReading,
  updateInventorizationRegisterReading,
} from '@/features/inventorization/domain/inventorization-reading';
import { acceptInventorizationReview } from '@/features/inventorization/domain/inventorization-review';
import type { InventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import { updateInventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import {
  BLOCK_STATUSES,
  CAPTURE_SESSION_STATUSES,
  type CaptureSession,
  READINESS_STATES,
  summarizeReadiness,
} from '@/shared/capture/capture-session';

interface InventorizationSessionState {
  session: CaptureSession | null;
  isLoading: boolean;
  error: string | null;
}

export function useInventorizationSession(sessionId: string) {
  const { captureSessions: repository } = useInventoryAppServices();
  const [state, setState] = useState<InventorizationSessionState>({
    session: null,
    isLoading: true,
    error: null,
  });

  const load = useCallback(async () => {
    setState(current => ({ ...current, isLoading: !current.session, error: null }));

    try {
      const session = await repository.get(sessionId);

      if (!session) {
        setState({ session: null, isLoading: false, error: `Draft ${sessionId} was not found.` });
        return;
      }

      setState({ session, isLoading: false, error: null });
    } catch (error) {
      setState({
        session: null,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unable to load inventorization draft.',
      });
    }
  }, [repository, sessionId]);

  async function saveUpdatedSession(
    updater: (session: CaptureSession, updatedAt: string) => CaptureSession,
    errorMessage: string,
    input: { markLocalChange?: boolean } = {},
  ) {
    try {
      const updatedAt = new Date().toISOString();
      const updated = await repository.update(sessionId, session => {
        const nextSession = dropIssuesResolvedByExtraction(updater(session, updatedAt));

        return input.markLocalChange ? markInventorizationSessionLocalChange(nextSession) : nextSession;
      });
      setState({ session: updated, isLoading: false, error: null });

      return updated;
    } catch (error) {
      setState(current => ({
        ...current,
        isLoading: false,
        error: error instanceof Error ? error.message : errorMessage,
      }));

      return undefined;
    }
  }

  async function markBlockValid(blockId: InventorizationBlockId) {
    await saveUpdatedSession(
      (session, updatedAt) => updateInventorizationBlock(session, blockId, BLOCK_STATUSES.valid, updatedAt),
      'Unable to update inventorization draft.',
    );
  }

  async function saveIdentity(patch: Partial<InventorizationIdentity>) {
    await saveUpdatedSession(
      (session, updatedAt) => updateInventorizationIdentity(session, patch, updatedAt),
      'Unable to save identity fields.',
      { markLocalChange: true },
    );
  }

  async function saveContext(patch: Partial<InventorizationContext>) {
    await saveUpdatedSession(
      (session, updatedAt) => updateInventorizationContext(session, patch, updatedAt),
      'Unable to save context fields.',
      { markLocalChange: true },
    );
  }

  async function saveMainReading(patch: Partial<InventorizationMainReading>) {
    await saveUpdatedSession(
      (session, updatedAt) => updateInventorizationMainReading(session, patch, updatedAt),
      'Unable to save main register reading.',
      { markLocalChange: true },
    );
  }

  async function saveRegisterReading(registerId: string, patch: Partial<InventorizationRegisterReading>) {
    await saveUpdatedSession(
      (session, updatedAt) => updateInventorizationRegisterReading(session, registerId, patch, updatedAt),
      'Unable to save register reading.',
      { markLocalChange: true },
    );
  }

  async function removeRegisterReading(registerId: string) {
    await saveUpdatedSession(
      (session, updatedAt) => removeInventorizationRegisterReading(session, registerId, updatedAt),
      'Unable to remove register reading.',
      { markLocalChange: true },
    );
  }

  async function savePosition(patch: Partial<InventorizationPosition>) {
    await saveUpdatedSession(
      (session, updatedAt) => updateInventorizationPosition(session, patch, updatedAt),
      'Unable to save position fields.',
      { markLocalChange: true },
    );
  }

  async function saveTechnicalDetails(patch: Partial<InventorizationTechnicalDetails>) {
    await saveUpdatedSession(
      (session, updatedAt) => updateInventorizationTechnicalDetails(session, patch, updatedAt),
      'Unable to save technical details.',
      { markLocalChange: true },
    );
  }

  function acceptReview() {
    return saveUpdatedSession(
      (session, updatedAt) => acceptInventorizationReview(session, updatedAt),
      'Unable to accept review.',
    );
  }

  useEffect(() => {
    void load();
  }, [load]);

  return {
    ...state,
    acceptReview,
    markBlockValid,
    refresh: load,
    saveContext,
    saveIdentity,
    saveMainReading,
    savePosition,
    removeRegisterReading,
    saveRegisterReading,
    saveTechnicalDetails,
  };
}

function markInventorizationSessionLocalChange(session: CaptureSession): CaptureSession {
  if (session.status !== CAPTURE_SESSION_STATUSES.queued && session.status !== CAPTURE_SESSION_STATUSES.synced) {
    return session;
  }

  const readiness = summarizeReadiness(session.blockStatuses);

  return {
    ...session,
    status:
      readiness.state === READINESS_STATES.ready ? CAPTURE_SESSION_STATUSES.ready : CAPTURE_SESSION_STATUSES.draft,
    submittedAt: undefined,
  };
}
